import * as React from 'react';
import { Checkbox, DefaultPalette, ICommandBarItemProps, Icon, Panel } from '@fluentui/react';
import TimeTaxSupportSystemClient from '../../system';
import { DateTime } from 'luxon';
import MessageCourier from '../../lib/MessageCourier';
import ViewWrapperWithCommandBar from '../Layout/ViewWrapperWithCommandBar';
import { defaultCommandBarButtonStyles, titleCommandBarButtonStyles } from '../../styles/button-styles';
import { useTranslation } from 'react-i18next';
import { Button } from 'devextreme-react/button';
import DataGrid, { Scrolling, Column, Export, SearchPanel, Summary, TotalItem } from 'devextreme-react/data-grid';
import { User } from '../../system/User';
import { EscalableExporterOptions, exportToExcel, getCorporativoInfo } from '../../lib/Util';
import { useBoolean } from '@fluentui/react-hooks';
import { ExportingEvent } from 'devextreme/ui/data_grid';
import { IReportHoursQuantified } from '../../system/reporting/reporte-horas-cuantificadas';
import InfoHourDecimalTooltip from '../../shared/components/InfoHourDecimalTooltip';
import { ReportingFilters } from './ReportingFilters';
import { Company } from '../../system/company/Company';
import { exportQuantifiedHoursPdf } from './exportQuantifiedHoursPdf';

interface IReportingQuantifiedHours {
  system: TimeTaxSupportSystemClient;
  courier: MessageCourier;
  changeBlockUI: (state: boolean) => void;
  user: User;
  setShowInsufficentPermissionsDialog: (state: boolean) => void;
}

type ColumnVisibilityMap = Record<string, boolean>;

const COLUMN_CONFIG = {
  pais: {
    key: 'Pais_empresa',
    label: 'País',
    defaultVisible: true,
  },
  empresa: {
    key: 'Empresa',
    label: 'Empresa distribuidas en la actividad',
    defaultVisible: true,
  },
  agrupador: {
    key: 'TIPO',
    label: 'Agrupador de liquidación - concepto',
    defaultVisible: true,
  },
  tipoConcepto: {
    key: 'Concepto',
    label: 'Tipo de concepto',
    defaultVisible: true,
  },
  areaProyecto: {
    key: 'principal_Area',
    label: 'Área del colaborador',
    defaultVisible: true,
  },
  codigoProyecto: {
    key: 'pro_codigo',
    label: 'Proyecto o Servicio Código',
    defaultVisible: true,
  },
  nombreProyecto: {
    key: 'Nombre_de_Servicio_o_Proyecto',
    label: 'Nombre de proyecto o servicio',
    defaultVisible: true,
  },
  nombreActividad: {
    key: 'act_nombre',
    label: 'Nombre de la actividad',
    defaultVisible: true,
  },
  horas: {
    key: 'Horas_Distribuido',
    label: 'Horas',
    defaultVisible: true,
  },
  costoPen: {
    key: 'COSTO_HORAS_ASIGNADAS_SOLES',
    label: 'Costo de horas Importe PEN',
    defaultVisible: true,
  },
  tipoCambio: {
    key: 'TIPO_CAMBIO',
    label: 'T.C.',
    defaultVisible: true,
  },
  costoUsd: {
    key: 'COSTO_HORAS_ASIGNADAS_DOLARES',
    label: 'Costo de horas Importe USD',
    defaultVisible: true,
  },
  periodo: {
    key: 'periodo',
    label: 'Periodo',
    defaultVisible: true,
  },
  tipoServicio: {
    key: 'Tipo_Area',
    label: 'Tipo Servicio',
    defaultVisible: true,
  },
};

export const ReportingQuantifiedHours = (props: IReportingQuantifiedHours) => {
  const { t } = useTranslation();
  const [period, setPeriod] = React.useState<DateTime | undefined>(undefined);
  const [items, setItems] = React.useState<IReportHoursQuantified[]>([]);
  const [isOpen, { setTrue: openPanel, setFalse: dismissPanel }] = useBoolean(false);
  const [visibleMap, setVisibleMap] = React.useState<ColumnVisibilityMap>({});
  const [isAticPeru, setIsAticPeru] = React.useState(false);
  const [isAticEspaña, setIsAticEspaña] = React.useState(false);
  const [companySelected, setCompanySelected] = React.useState<Company | null>(null);
  const [node, setNode] = React.useState<string>('');
  const gridRef = React.useRef<DataGrid>(null);

  const itemsMenu = React.useMemo(
    (): ICommandBarItemProps[] => [
      {
        key: 'title',
        text: t('Horas cuantificadas'),
        iconProps: {
          iconName: 'CRMReport',
          color: DefaultPalette.themeLighterAlt,
          className: 'text-title-btn',
        },
        buttonStyles: titleCommandBarButtonStyles,
        className: 'text-title-btn',
      },
    ],
    [],
  );

  React.useEffect(() => {
    const corporativoInfo = getCorporativoInfo();
    if (corporativoInfo) {
      const NRO_ADMIN_TRIBUTARIA_GE_ATIC_ESPANIA = 'A84570290';
      const NRO_ADMIN_TRIBUTARIA_GE_ATIC_PERU = '20451539087';
      // Si estos dos corportivos no estan asociados al corporativo del usuario, se muestra el dialog de permisos insuficientes
      if (![NRO_ADMIN_TRIBUTARIA_GE_ATIC_ESPANIA, NRO_ADMIN_TRIBUTARIA_GE_ATIC_PERU].includes(corporativoInfo.empp_nro_admin_tributaria)) {
        props.setShowInsufficentPermissionsDialog(true);
      }

      if (corporativoInfo.empp_nro_admin_tributaria === NRO_ADMIN_TRIBUTARIA_GE_ATIC_PERU) {
        setIsAticPeru(true);
      }

      if (corporativoInfo.empp_nro_admin_tributaria === NRO_ADMIN_TRIBUTARIA_GE_ATIC_ESPANIA) {
        setIsAticEspaña(true);
      }
    }
  }, []);

  /* ============================
       HELPERS
    ============================ */
  const getVisible = React.useCallback((key: string) => visibleMap[key] ?? false, [visibleMap]);

  const buildInitialVisibility = () => {
    const map: ColumnVisibilityMap = {};

    Object.values(COLUMN_CONFIG).forEach((c) => {
      map[c.key] = c.defaultVisible;
    });

    setVisibleMap(map);
  };

  const markAll = () => setVisibleMap((prev) => Object.fromEntries(Object.keys(prev).map((k) => [k, true])));

  const unmarkAll = () => setVisibleMap((prev) => Object.fromEntries(Object.keys(prev).map((k) => [k, false])));

  /* ============================
       SEARCH
    ============================ */
  const onClickSearch = async () => {
    props.changeBlockUI(true);
    try {
      if (!period) return;
      setItems([]);

      const data = await props.system.getReportingSystem().getQuantifiedHoursReport(period, companySelected?.getId(), node);

      setItems(data);
      buildInitialVisibility();
      if (data.length === 0) {
        props.courier.messageInfo(t('No se encontraron resultados para el periodo seleccionado'));
        return;
      }

      props.courier.messageSuccess(t('Reporte generado con éxito'));
    } catch (e) {
      props.courier.messageError(e);
    } finally {
      props.changeBlockUI(false);
    }
  };
  const onExporting = (e: ExportingEvent<IReportHoursQuantified, any>) => {
    const corporativoInfo = getCorporativoInfo();

    let options: EscalableExporterOptions = {
      fileName: `reporte-de-horas-cuantificadas-del-periodo-${period?.toFormat('LL-yyyy')}`,
      sheetName: t('Horas cuantificadas'),
      title: t('Reporte de Horas cuantificadas'),
      extraInfoRows: [
        { label: t('Periodo'), value: period ? period.toFormat('yyyy-LL') : '' },
        { label: t('Corporativo'), value: corporativoInfo?.empp_nombre || '' },
      ],
      customizeCell: (options) => {
        if (options.gridCell?.rowType === 'group') {
          options.excelCell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: '8DB4E2' } };
        }
        if (options.gridCell?.rowType === 'totalFooter' && options.excelCell.value) {
          options.excelCell.font.italic = true;
        }
      },
    };
    exportToExcel(e, options);
  };

  const getFilteredData = async (): Promise<IReportHoursQuantified[]> => {
    const instance = gridRef.current?.instance;
    if (!instance) return [];

    const filter = instance.getCombinedFilter(true);

    const data = (await instance.getDataSource().store().load({
      filter,
    })) as IReportHoursQuantified[];

    return data;
  };

  return (
    <ViewWrapperWithCommandBar
      withBorderBottom
      title={''}
      items={itemsMenu}
      overflowButtonStyles={defaultCommandBarButtonStyles}
      variant="highligth"
      titleBody={t('Horas cuantificadas')}
    >
      <div className="padding-1 bg-white">
        <div className="ms-Grid" dir="ltr">
          <div className="ms-Grid-row">
            <ReportingFilters
              system={props.system}
              courier={props.courier}
              onChangeFromDate={(d) => {
                const fecha = d.toJSDate();
                const start = DateTime.fromJSDate(new Date(fecha.getFullYear(), fecha.getMonth(), 1));
                setPeriod(start);
              }}
              onChangeToDate={(d) => {}}
              onChangeCompany={(c) => {
                if (c instanceof Company) setCompanySelected(c);
                else setCompanySelected(null);
              }}
              onClickSearch={onClickSearch}
              onChangeNode={(nodeId) => {
                setNode(nodeId);
              }}
              showSingleCompanyFilter={true}
              showPeriodFilter={true}
              showSectorFilter={true}
              user={props.user}
              companyLabel={t('Seleccionar empresas')}
              sectorLabel={t('Área del colaborador')}
              periodLabel={t('Periodo')}
            />

            {isAticPeru && (
              <div className="ms-Grid-col ms-sm12">
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'row',
                    alignItems: 'center',
                    marginTop: 20,
                  }}
                >
                  <Icon iconName="Info" style={{ marginRight: '5px' }} />
                  <div className="ml-2">
                    <li className="mb-1" style={{ fontWeight: 'normal' }}>
                      {t(
                        'El costo de horas en USD se calcula tomando el monto en soles (PEN) y dividiéndolo entre el último Tipo de Cambio Venta publicado por SUNAT.',
                      )}
                    </li>
                  </div>
                </div>
              </div>
            )}
          </div>
          {items.length > 0 && (
            <div className="ms-Grid-row">
              <div className="ms-Grid-col ms-sm12">
                <Button
                  className="btn-dinamic-table"
                  icon="inserttable"
                  onClick={openPanel}
                  style={{
                    marginRight: 12,
                  }}
                />
                <span title={t("Exportar datos a pdf")}>
                  <Button
                    className="btn-dinamic-table"
                    icon="pdffile"
                    onClick={() => exportQuantifiedHoursPdf({ period, getFilteredData, node, company: companySelected })}
                    style={{
                      marginRight: 60,
                    }}
                  />
                </span>
                <DataGrid
                  ref={gridRef}
                  height={620}
                  width="100%"
                  dataSource={items}
                  showBorders={true}
                  wordWrapEnabled={true}
                  onExporting={onExporting}
                >
                  <Export enabled={true} />
                  <Scrolling mode="virtual" />
                  <SearchPanel visible={true} placeholder={t('Buscar...')} />

                  <Column
                    minWidth={80}
                    dataField={COLUMN_CONFIG.pais.key}
                    caption={t(COLUMN_CONFIG.pais.label)}
                    visible={getVisible(COLUMN_CONFIG.pais.key)}
                    groupCellRender={(d) => {
                      return <div className="dx-datagrid-summary-item dx-datagrid-text-content dx-datagrid-group-text-content">{d.value}</div>;
                    }}
                  />

                  <Column
                    minWidth={220}
                    dataField={COLUMN_CONFIG.empresa.key}
                    caption={t(COLUMN_CONFIG.empresa.label)}
                    visible={getVisible(COLUMN_CONFIG.empresa.key)}
                    groupCellRender={(d) => {
                      return <div className="dx-datagrid-summary-item dx-datagrid-text-content dx-datagrid-group-text-content">{d.value}</div>;
                    }}
                  />

                  <Column
                    minWidth={150}
                    dataField={COLUMN_CONFIG.agrupador.key}
                    caption={t(COLUMN_CONFIG.agrupador.label)}
                    visible={getVisible(COLUMN_CONFIG.agrupador.key)}
                    alignment={'center'}
                    cellRender={(d) => {
                      return <div className="dx-datagrid-summary-item dx-datagrid-text-content dx-datagrid-group-text-content">{d.value}</div>;
                    }}
                  />

                  <Column
                    minWidth={160}
                    dataField={COLUMN_CONFIG.areaProyecto.key}
                    caption={t(COLUMN_CONFIG.areaProyecto.label)}
                    visible={getVisible(COLUMN_CONFIG.areaProyecto.key)}
                    alignment={'center'}
                    cellRender={(d) => {
                      return <div className="dx-datagrid-summary-item dx-datagrid-text-content dx-datagrid-group-text-content">{d.value}</div>;
                    }}
                  />

                  <Column
                    minWidth={140}
                    dataField={COLUMN_CONFIG.tipoConcepto.key}
                    caption={t(COLUMN_CONFIG.tipoConcepto.label)}
                    visible={getVisible(COLUMN_CONFIG.tipoConcepto.key)}
                    alignment={'center'}
                    cellRender={(d) => {
                      return <div className="dx-datagrid-summary-item dx-datagrid-text-content dx-datagrid-group-text-content">{d.value}</div>;
                    }}
                  />

                  <Column
                    minWidth={120}
                    dataField={COLUMN_CONFIG.codigoProyecto.key}
                    caption={t(COLUMN_CONFIG.codigoProyecto.label)}
                    visible={getVisible(COLUMN_CONFIG.codigoProyecto.key)}
                    alignment={'center'}
                    cellRender={(d) => {
                      return <div className="dx-datagrid-summary-item dx-datagrid-text-content dx-datagrid-group-text-content">{d.value}</div>;
                    }}
                  />

                  <Column
                    minWidth={160}
                    dataField={COLUMN_CONFIG.nombreProyecto.key}
                    caption={t(COLUMN_CONFIG.nombreProyecto.label)}
                    visible={getVisible(COLUMN_CONFIG.nombreProyecto.key)}
                    groupCellRender={(d) => {
                      return <div className="dx-datagrid-summary-item dx-datagrid-text-content dx-datagrid-group-text-content">{d.value}</div>;
                    }}
                  />

                  <Column
                    minWidth={140}
                    dataField={COLUMN_CONFIG.nombreActividad.key}
                    caption={t(COLUMN_CONFIG.nombreActividad.label)}
                    visible={getVisible(COLUMN_CONFIG.nombreActividad.key)}
                    alignment={'center'}
                    cellRender={(d) => {
                      return <div className="dx-datagrid-summary-item dx-datagrid-text-content dx-datagrid-group-text-content">{d.value}</div>;
                    }}
                  />

                  <Column
                    minWidth={100}
                    dataField={COLUMN_CONFIG.horas.key}
                    caption={t(COLUMN_CONFIG.horas.label)}
                    visible={getVisible(COLUMN_CONFIG.horas.key)}
                    alignment={'center'}
                    width={100}
                    headerCellRender={() => {
                      return (
                        <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', justifyContent: 'center' }}>
                          {t(COLUMN_CONFIG.horas.label)}
                          <InfoHourDecimalTooltip />
                        </div>
                      );
                    }}
                    calculateCellValue={(data) => {
                      const horas = data[COLUMN_CONFIG.horas.key];
                      return parseFloat(horas).toFixed(4);
                    }}
                    cellRender={(d) => {
                      return <div className="dx-datagrid-summary-item dx-datagrid-text-content dx-datagrid-group-text-content">{d.value}</div>;
                    }}
                  />

                  {!isAticEspaña && (
                    <Column
                      minWidth={100}
                      dataField={COLUMN_CONFIG.costoPen.key}
                      caption={t(COLUMN_CONFIG.costoPen.label)}
                      visible={getVisible(COLUMN_CONFIG.costoPen.key)}
                      alignment={'center'}
                      width={200}
                      calculateCellValue={(data) => {
                        const costoPen = data[COLUMN_CONFIG.costoPen.key];
                        if (costoPen === '-') return costoPen;
                        return parseFloat(costoPen).toFixed(3);
                      }}
                      cellRender={(d) => {
                        return <div className="dx-datagrid-summary-item dx-datagrid-text-content dx-datagrid-group-text-content">{d.value}</div>;
                      }}
                    />
                  )}

                  {!isAticEspaña && (
                    <Column
                      minWidth={100}
                      dataField={COLUMN_CONFIG.tipoCambio.key}
                      caption={t(COLUMN_CONFIG.tipoCambio.label)}
                      visible={getVisible(COLUMN_CONFIG.tipoCambio.key)}
                      alignment={'center'}
                      width={50}
                      calculateCellValue={(data) => {
                        const tipoCambio = data[COLUMN_CONFIG.tipoCambio.key];
                        if (tipoCambio === '-') return tipoCambio;
                        return parseFloat(tipoCambio).toFixed(3);
                      }}
                      cellRender={(d) => {
                        return <div className="dx-datagrid-summary-item dx-datagrid-text-content dx-datagrid-group-text-content">{d.value}</div>;
                      }}
                    />
                  )}

                  <Column
                    minWidth={100}
                    dataField={COLUMN_CONFIG.costoUsd.key}
                    caption={t(COLUMN_CONFIG.costoUsd.label)}
                    visible={getVisible(COLUMN_CONFIG.costoUsd.key)}
                    alignment={'center'}
                    width={200}
                    calculateCellValue={(data) => {
                      const costoUsd = data[COLUMN_CONFIG.costoUsd.key];
                      if (costoUsd === '-') return costoUsd;
                      return parseFloat(costoUsd).toFixed(3);
                    }}
                    cellRender={(d) => {
                      return <div className="dx-datagrid-summary-item dx-datagrid-text-content dx-datagrid-group-text-content">{d.value}</div>;
                    }}
                  />

                  <Column
                    minWidth={100}
                    dataField={COLUMN_CONFIG.periodo.key}
                    caption={t(COLUMN_CONFIG.periodo.label)}
                    visible={getVisible(COLUMN_CONFIG.periodo.key)}
                    alignment={'center'}
                    width={100}
                    cellRender={(d) => {
                      return <div className="dx-datagrid-summary-item dx-datagrid-text-content dx-datagrid-group-text-content">{d.value}</div>;
                    }}
                  />

                  <Column
                    minWidth={50}
                    dataField={COLUMN_CONFIG.tipoServicio.key}
                    caption={t(COLUMN_CONFIG.tipoServicio.label)}
                    visible={getVisible(COLUMN_CONFIG.tipoServicio.key)}
                    alignment={'center'}
                    width={120}
                    cellRender={(d) => {
                      return <div className="dx-datagrid-summary-item dx-datagrid-text-content dx-datagrid-group-text-content">{d.value}</div>;
                    }}
                  />

                  <Summary>
                    <TotalItem
                      column={COLUMN_CONFIG.horas.key}
                      summaryType="sum"
                      valueFormat={{
                        type: 'fixedPoint',
                        precision: 2,
                      }}
                      displayFormat={`${t('Total')}: {0}`}
                    />
                  </Summary>
                </DataGrid>
              </div>
            </div>
          )}
        </div>

        <Panel headerText={t('Tabla dinamica')} isOpen={isOpen} onDismiss={dismissPanel} closeButtonAriaLabel={t('Close')}>
          <div>
            <p>{t('Selecciona las columnas que quieres ver')}</p>
            <span className="d-block link-styles" onClick={markAll}>
              {t('Marcar todas')}
            </span>
            <span className="d-block link-styles mb-2" onClick={unmarkAll}>
              {t('Desmarcar todas')}
            </span>
            {Object.values(COLUMN_CONFIG).map((c) => (
              <Checkbox
                className="d-block mt-1"
                key={c.key}
                label={t(c.label)}
                checked={visibleMap[c.key]}
                onChange={() => setVisibleMap((prev) => ({ ...prev, [c.key]: !prev[c.key] }))}
              />
            ))}
          </div>
        </Panel>
      </div>
    </ViewWrapperWithCommandBar>
  );
};
